/**
 * Catalogo aggregato degli incantesimi DWR trascritti.
 *
 * Le spell list sono organizzate per **classe-source** (la classe a cui lo
 * spell appartiene per gameplay): `magoSpells`, `chiericoSpells`,
 * `druidoSpells`, `warlockSpells`. Il Paladino accede tramite estensione
 * `availableTo` sugli spell di Chierico/Druido (3 Tradizioni: Negromanzia
 * e Valore derivati dal Chierico; Natura solo se archetipo Elfo, deriva
 * dal Druido). Lo schema `Spell` ha già il campo `availableTo:
 * SpellAccess[]` che permette a un singolo oggetto spell di essere
 * accessibile da più classi/tradizioni (es. *Luce* per Mago + tutte le 5
 * Tradizioni del Chierico + Druido + Paladino-Natura).
 *
 * **Stato attuale (post-D4)**:
 * - Mago: 3 L0 + 8 L1 + 7 L3 + 7 L5 + 7 L7 + 5 L9 = 37 spell
 * - Chierico: 3 L0 + 13 L1 + 10 L3 + 8 L5 + 6 L7 + 5 L9 = 45 spell
 *   (5 Tradizioni Sacre)
 * - Druido: 1 L0 + 1 L1 + 1 L3 + 2 L5 + 1 L7 + 1 L9 = 7 spell nuovi
 *   (gli altri sono estesi su Mago/Chierico tramite `availableTo`)
 * - Warlock: 1 L0 + 0 L1 + 0 L3+ = 1 spell nuovo (gli altri sono tutti
 *   estesi; il Warlock non ha spell L7/L9 propri — accede a L7/L9 solo
 *   via `Rivelazioni Proibite` con grant cross-class)
 *
 * **Totale catalogo post-D4**: 90 spell unici (35 di D3, +25 di D4).
 */

import type { ClassId } from '../../schemas/classId';
import type { Spell } from '../../schemas/spell';
import { chiericoSpells } from './chierico';
import { druidoSpells } from './druido';
import { magoSpells } from './mago';
import { warlockSpells } from './warlock';

export { chiericoSpells, druidoSpells, magoSpells, warlockSpells };
export {
  magoCantrips,
  magoLevel1Spells,
  magoLevel3Spells,
  magoLevel5Spells,
  magoLevel7Spells,
  magoLevel9Spells,
} from './mago';
export {
  chiericoCantrips,
  chiericoLevel1Spells,
  chiericoLevel3Spells,
  chiericoLevel5Spells,
  chiericoLevel7Spells,
  chiericoLevel9Spells,
} from './chierico';
export {
  druidoCantrips,
  druidoLevel1Spells,
  druidoLevel3Spells,
  druidoLevel5Spells,
  druidoLevel7Spells,
  druidoLevel9Spells,
} from './druido';
export { warlockCantrips, warlockLevel1Spells } from './warlock';

/** Tutti gli spell trascritti, indipendentemente dalla classe. */
export const allSpells: readonly Spell[] = [
  ...magoSpells,
  ...chiericoSpells,
  ...druidoSpells,
  ...warlockSpells,
];

/**
 * Cerca uno spell per ID nel catalogo aggregato.
 * Restituisce `undefined` se l'ID non esiste.
 */
export function findSpellById(spellId: string): Spell | undefined {
  return allSpells.find((s) => s.id === spellId);
}

/**
 * Ritorna tutti gli spell accessibili a una specifica classe (in qualsiasi
 * tradizione/configurazione). La verifica usa `Spell.availableTo`: lo spell
 * è incluso se almeno una `SpellAccess` ha `classId` corrispondente.
 *
 * Per le classi con tradizioni (Chierico/Paladino), il filtraggio per
 * tradizione si fa con `findSpellsByClassAndTradition` qui sotto.
 */
export function findSpellsByClass(classId: ClassId): readonly Spell[] {
  return allSpells.filter((s) => s.availableTo.some((a) => a.classId === classId));
}

/**
 * Ritorna tutti gli spell accessibili a una specifica classe **in una
 * specifica Tradizione Sacra**.
 *
 * Usato per le classi con `tradizioni` (Chierico, Druido, Paladino): un
 * Chierico Crepuscolo accede a tutti gli spell che hanno una `SpellAccess`
 * `{ classId: 'chierico', tradizione: 'crepuscolo' }`. Per il Mago/Warlock
 * (senza tradizioni) usare `findSpellsByClass`.
 *
 * Il filtro NON applica `requiresArchetype`: quello è un vincolo di
 * rendering UI separato (vedi D10/FI-7).
 */
export function findSpellsByClassAndTradition(
  classId: ClassId,
  tradizione: string,
): readonly Spell[] {
  return allSpells.filter((s) =>
    s.availableTo.some(
      (a) =>
        a.classId === classId &&
        'tradizione' in a &&
        (a as { tradizione: string }).tradizione === tradizione,
    ),
  );
}
